import { useAtom } from 'jotai';
import { useEffect } from 'react';

import { spinwheelLiabilitiesAtom } from '../../bills/state/BillsAtoms';
import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import { requestBuilder } from '../../http/RequestBuilder';

interface Liability {
    id: string;
    type: string;
    creditor: string;
    accountNumber: string;
    balance: number;
    monthlyPayment: number;
    interestRate?: number;
    status: string;
    openedDate?: string;
    lastPaymentDate?: string;
    logoUrl?: string;
}

interface LiabilitiesByType {
    creditCards: Liability[];
    autoLoans: Liability[];
    homeLoans: Liability[];
    personalLoans: Liability[];
    studentLoans: Liability[];
    miscellaneousLiabilities: Liability[];
}

interface LiabilitiesResponseDto {
    totalMonthlyMinimum: number;
    totalOutstandingBalance: number;
    liabilities: LiabilitiesByType;
    reportDate: number;
    summary: {
        creditCards: unknown;
        autoLoans: unknown;
        homeLoans: unknown;
        personalLoans: unknown;
        studentLoans: unknown;
        miscellaneousLiabilities: unknown;
    };
}

const useFetchLiabilities = requestBuilder.query<LiabilitiesResponseDto>({
    endpoint: '/api/spinwheel/liabilities',
    queryKey: 'getLiabilities',
});

export const useGetLiabilities = () => {
    const user = useAuthedUser();
    const [liabilitiesData, setLiabilitiesData] = useAtom(
        spinwheelLiabilitiesAtom,
    );

    // Fetch liabilities if user has spinwheelId and we don't have data yet
    // Note: If spinwheelOverride is true, we're skipping Spinwheel so NEVER fetch liabilities
    // Check spinwheelOverride FIRST - if it's true, never fetch regardless of other conditions
    if (user?.spinwheelOverride === true) {
        return {
            data: liabilitiesData,
            refetch: async () => {},
            isRefetching: false,
            isLoading: false,
            isError: false,
        };
    }

    const shouldFetchLiabilities =
        !(user?.spinwheelId == null) &&
        (!liabilitiesData ||
            Object.values(liabilitiesData.liabilities).every(
                (type: Liability[]) => type.length === 0,
            ));

    const {
        data: fetchedLiabilitiesData,
        refetch,
        isRefetching,
        isLoading,
        isError,
    } = useFetchLiabilities(
        {},
        {
            enabled: shouldFetchLiabilities,
        },
    );

    useEffect(() => {
        if (fetchedLiabilitiesData) setLiabilitiesData(fetchedLiabilitiesData);
    }, [fetchedLiabilitiesData]);

    return {
        data: liabilitiesData,
        refetch,
        isRefetching,
        isLoading,
        isError,
    };
};
